import { createSharedComposable } from '@vueuse/core'

import { contentDisplaySettingsSchema } from './index.types'
import type { ContentDisplaySettings } from './index.types'

type UseFeatureFlagData = {
  featureFlags: ContentDisplaySettings
}

const FEATURE_FLAG_LOCAL_STORAGE_KEY = 'app:featureFlags'

export type FeatureFlags = ContentDisplaySettings

function loadFromLocalStorage(): ContentDisplaySettings {
  const localStorageFeatureFlag =
    localStorage.getItem(FEATURE_FLAG_LOCAL_STORAGE_KEY) ?? ''
  try {
    const localFeatureFlag = JSON.parse(localStorageFeatureFlag)
    return parseFeatureFlagsOrDefault(localFeatureFlag)
  } catch {
    return toRaw(useAppConfig().defaultFeatureFlags)
  }
}

function saveToLocalStorage(featureFlags: ContentDisplaySettings) {
  const featureFlagStr = JSON.stringify(
    parseFeatureFlagsOrDefault(featureFlags)
  )
  localStorage.setItem(FEATURE_FLAG_LOCAL_STORAGE_KEY, featureFlagStr)
}

function parseFeatureFlagsOrDefault(values: unknown): ContentDisplaySettings {
  const parsedData = contentDisplaySettingsSchema.safeParse(values)
  if (!parsedData.success) {
    console.warn('Invalid Feature Flags', parsedData.error)
    return toRaw(useAppConfig().defaultFeatureFlags)
  }
  return parsedData.data
}

/**
 * Shared composable that returns current feature flags.
 * On mount, updates local storage with new data from Assertive Yield if available.
 */
export const useFeatureFlags = createSharedComposable(
  (): UseFeatureFlagData => {
    const contentDisplaySettingsStore = useContentDisplaySettingsStore()

    const existingFeatureFlags = loadFromLocalStorage()

    async function fetchFeatureFlags() {
      try {
        await contentDisplaySettingsStore.initialized
        saveToLocalStorage(
          parseFeatureFlagsOrDefault(contentDisplaySettingsStore.featureFlags)
        )
      } catch {
        console.warn('Feature flags not retrieved before timeout.')
      }
    }

    fetchFeatureFlags()
    return { featureFlags: existingFeatureFlags }
  }
)
